<script setup lang="ts">
import type { TooltipProps } from '#ui/types';
import { useRafFn, useResizeObserver } from '@vueuse/core';
import { computed, onMounted, useTemplateRef, watch } from 'vue';
import DTooltip from '#layers/ui/components/Tooltip.vue';
import { useTruncation } from '#layers/ui/composables/use-truncation';
import { truncatedTextTooltipUi } from '#layers/ui/composables/use-truncation-tooltip';

interface Props {
	text: string;
	tooltipUi?: TooltipProps['ui'];
}

const { text, tooltipUi } = defineProps<Props>();

const ui = computed(() => ({
	...tooltipUi,
	content: [truncatedTextTooltipUi.content, tooltipUi?.content],
	text: [truncatedTextTooltipUi.text, tooltipUi?.text],
}));

const contentRef = useTemplateRef<HTMLElement>('content');
const { isTruncated, measure } = useTruncation(contentRef);

const { resume: scheduleMeasure } = useRafFn(measure, { immediate: false, once: true });

onMounted(() => {
	scheduleMeasure();
	// Web fonts change text width once loaded.
	document.fonts?.ready.then(scheduleMeasure);
});

watch(() => text, () => scheduleMeasure());

useResizeObserver(contentRef, scheduleMeasure);
</script>

<template>
	<DTooltip :text :ui :disabled="!isTruncated || !text">
		<span
			ref="content"
			class="block truncate"
			:tabindex="isTruncated && text ? 0 : undefined"
		>
			{{ text }}
		</span>
	</DTooltip>
</template>
